import React, { useState, useRef, useEffect } from 'react';
import './VigilDesignReport.css';

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  SECTIONS                                                                  */
/* ═══════════════════════════════════════════════════════════════════════════ */
const SECTIONS = [
  { id: 'executive',      icon: '📊', title: 'Executive Summary' },
  { id: 'architecture',   icon: '🏗️', title: 'Architecture Overview' },
  { id: 'grammar',        icon: '📐', title: 'DSL Grammar Specification' },
  { id: 'queryTypes',     icon: '🔎', title: 'Query Types Reference' },
  { id: 'metavars',       icon: '🔗', title: 'Metavariables & Expressions' },
  { id: 'ruleSpec',       icon: '📄', title: 'Rule File Specification' },
  { id: 'validation',     icon: '✅', title: 'Validation Pipeline' },
  { id: 'compilation',    icon: '⚙️', title: 'Compilation Architecture' },
  { id: 'engines',        icon: '⚡', title: 'Engine Support Matrix' },
  { id: 'scanning',       icon: '🔄', title: 'Scanning Pipeline' },
  { id: 'inventory',      icon: '📦', title: 'Rule Inventory' },
  { id: 'dataStructures', icon: '🗂️', title: 'Data Structures' },
  { id: 'taint',          icon: '🔀', title: 'Taint Analysis' },
  { id: 'concepts',       icon: '🧩', title: 'Concept System' },
  { id: 'decisions',      icon: '💡', title: 'Design Decisions & Rationale' },
];

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  HELPERS                                                                   */
/* ═══════════════════════════════════════════════════════════════════════════ */
function SectionHeading({ icon, title }) {
  return (
    <h2 className="vdr-section-heading">
      <span>{icon}</span> {title}
    </h2>
  );
}

function CodeBlock({ code }) {
  return (
    <pre className="vdr-code-block">
      <code>{code}</code>
    </pre>
  );
}

function BulletList({ items }) {
  if (!items || !Array.isArray(items)) return null;
  return (
    <ul className="vdr-bullets">
      {items.map((item, i) => <li key={i}>{item}</li>)}
    </ul>
  );
}

function Table({ headers, rows, mono }) {
  return (
    <div className="vdr-table-wrapper">
      <table className="vdr-table">
        <thead>
          <tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td key={ci} className={mono && mono.includes(ci) ? 'mono' : ''}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DiagramBlock({ code }) {
  return <pre className="vdr-diagram">{code}</pre>;
}

function SourceFiles({ files }) {
  return (
    <div className="vdr-source-files">
      {files.map((f, i) => <span key={i} className="vdr-source-pill">{f}</span>)}
    </div>
  );
}

function SubHeading({ children }) {
  return <h4 className="vdr-subheading">{children}</h4>;
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  MAIN COMPONENT                                                            */
/* ═══════════════════════════════════════════════════════════════════════════ */
export default function VigilDesignReport() {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const observerRef = useRef(null);

  useEffect(() => {
    const handleIntersect = (entries) => {
      const visible = entries.filter(e => e.isIntersecting);
      if (visible.length > 0) {
        setActiveId(visible[0].target.id);
      }
    };
    observerRef.current = new IntersectionObserver(handleIntersect, {
      rootMargin: '-60px 0px -70% 0px',
      threshold: 0,
    });
    SECTIONS.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observerRef.current.observe(el);
    });
    return () => observerRef.current?.disconnect();
  }, []);

  const renderSection = (section) => {
    switch (section.id) {

      /* ──────────────────────────────────────────────────────────────────── */
      case 'executive':
        return (
          <>
            <p className="vdr-body-text">
              This document provides a comprehensive design review of the Vigil SAST engine's
              Domain-Specific Language (DSL). It covers the grammar specification, compilation
              pipeline, engine integration, rule inventory, and key architectural decisions.
              Intended for expert review and technical due diligence.
            </p>

            <div className="vdr-stat-row">
              <div className="vdr-stat-card"><div className="vdr-stat-value">998</div><div className="vdr-stat-label">Rules</div></div>
              <div className="vdr-stat-card"><div className="vdr-stat-value">99</div><div className="vdr-stat-label">Rule Packs</div></div>
              <div className="vdr-stat-card"><div className="vdr-stat-value">10</div><div className="vdr-stat-label">Languages</div></div>
              <div className="vdr-stat-card"><div className="vdr-stat-value">4</div><div className="vdr-stat-label">Engines</div></div>
            </div>

            <SubHeading>Vigil vs Semgrep — Rule Coverage Comparison</SubHeading>
            <p className="vdr-body-text">
              Vigil beats Semgrep in ALL 10 supported languages:
            </p>
            <Table
              headers={['Language', 'Vigil', 'Semgrep', 'Δ']}
              rows={[
                ['JavaScript',  '360', '350', '+10'],
                ['TypeScript',  '302', '300', '+2'],
                ['Python',      '254', '250', '+4'],
                ['Java',        '205', '200', '+5'],
                ['Go',          '168', '150', '+18'],
                ['PHP',         '102', '100', '+2'],
                ['Ruby',         '88',  '80', '+8'],
                ['C#',           '84',  '60', '+24'],
                ['C',            '34',  '30', '+4'],
                ['C++',          '31',  '30', '+1'],
              ]}
              mono={[1, 2, 3]}
            />
          </>
        );

      /* ──────────────────────────────────────────────────────────────────── */
      case 'architecture':
        return (
          <>
            <p className="vdr-body-text">
              The Vigil pipeline transforms human-readable DSL rule files through parsing,
              validation, and compilation stages before dispatching to one of four analysis engines.
            </p>

            <DiagramBlock>{`┌─────────────┐    ┌──────────────┐    ┌────────────┐    ┌──────────────┐    ┌─────────────┐
│  Rule Files │───▶│  DSL Parser  │───▶│ Validation │───▶│  Compiler    │───▶│  Engine     │
│  (.rule)    │    │ lexer+parser │    │ validator  │    │ per-engine   │    │  Execution  │
└─────────────┘    └──────────────┘    └────────────┘    └──────────────┘    └─────────────┘
                                                                │
                                                    ┌───────────┼───────────┐
                                                    ▼           ▼           ▼
                                              ┌──────────┐ ┌────────┐ ┌──────────┐
                                              │ Opengrep │ │ Joern  │ │TreeSitter│
                                              └──────────┘ └────────┘ └──────────┘`}</DiagramBlock>

            <SubHeading>Key Source Files</SubHeading>
            <Table
              headers={['Module', 'Files']}
              rows={[
                ['DSL',       'pkg/dsl/{lexer,parser,query}.go'],
                ['Rules',     'pkg/rule/{types,validator}.go'],
                ['Compilers', 'pkg/compiler/{compiler,opengrep,joern,rascal,treesitter}.go'],
                ['Engines',   'pkg/engine/{engine,selector,registry}.go'],
                ['CLI',       'cmd/vigil/{scan,compile,loader,rule}.go'],
                ['Concepts',  'pkg/concept/{mapping,catalog}.go'],
              ]}
              mono={[1]}
            />
          </>
        );

      /* ──────────────────────────────────────────────────────────────────── */
      case 'grammar':
        return (
          <>
            <p className="vdr-body-text">
              The Vigil DSL is a declarative query language designed for expressing static analysis
              patterns. The grammar is parsed by a hand-written recursive-descent parser.
            </p>

            <CodeBlock code={`query        ::= "find" find_type [target] [clauses]
find_type    ::= "call" ["to"] | "usage" ["of"] | "pattern" | "literal" | 
                 "function" | "import" | "class" | "annotation" | 
                 "variable" | "assignment" | "ref" | "flow"
target       ::= expr
clauses      ::= [from_clause] [to_clause] [where_clause] [within_clause] 
                 [in_clause]
from_clause  ::= "from" expr
to_clause    ::= "to" expr
where_clause ::= "where" condition
within_clause::= "within" expr
in_clause    ::= "in" ("language" "(" lang_list ")" | "file" "(" glob ")")

expr         ::= string_literal | concept_ref | any_expr | all_expr | ident
concept_ref  ::= "@" identifier
any_expr     ::= "any" "(" expr_list ")"
all_expr     ::= "all" "(" expr_list ")"
expr_list    ::= expr ("," expr)*

condition    ::= and_cond | or_cond | not_cond | atomic_cond
and_cond     ::= condition "and" condition
or_cond      ::= condition "or" condition
not_cond     ::= "not" condition
atomic_cond  ::= "uses" "(" expr ")" | "passes_through" "(" expr ")" |
                 "calls" expr | "matches" "(" string ")" |
                 "extends" expr | "implements" expr`} />

            <SubHeading>Parser Implementation</SubHeading>
            <SourceFiles files={[
              'pkg/dsl/lexer.go (407 lines)',
              'pkg/dsl/parser.go (1073 lines)',
              'pkg/dsl/query.go',
            ]} />
            <p className="vdr-body-text">
              Key functions: <code>ParseQuery()</code>, <code>ParseRuleFile()</code>
            </p>
          </>
        );

      /* ──────────────────────────────────────────────────────────────────── */
      case 'queryTypes':
        return (
          <>
            <p className="vdr-body-text">
              All DSL queries begin with <code>find</code> followed by a query type keyword.
              The distribution of query types across 998 rules:
            </p>

            <Table
              headers={['Query Type', 'Count', '%', 'Purpose']}
              rows={[
                [<code>find call to</code>,     '629', '63%', 'Match function/method calls'],
                [<code>find usage of</code>,    '322', '32%', 'Match identifier usage/references'],
                [<code>find pattern</code>,      '36',  '4%', 'Match arbitrary code patterns'],
                [<code>find literal</code>,      '12',  '1%', 'Match literal values'],
                [<code>find annotation</code>,    '1', '<1%', 'Match annotations/decorators'],
                [<code>find function</code>,      '—',   '—', 'Match function declarations'],
                [<code>find import</code>,        '—',   '—', 'Match import statements'],
                [<code>find class</code>,         '—',   '—', 'Match class/type declarations'],
                [<code>find variable</code>,      '—',   '—', 'Match variable declarations'],
                [<code>find assignment</code>,    '—',   '—', 'Match assignment statements'],
              ]}
              mono={[1, 2]}
            />

            <SubHeading>find call to</SubHeading>
            <p className="vdr-body-text">Matches function and method call expressions:</p>
            <CodeBlock code={`query: |
  find call to any("eval(...)", "new Function(...)")`} />

            <SubHeading>find usage of</SubHeading>
            <p className="vdr-body-text">Matches identifier usage and references, including JSX patterns:</p>
            <CodeBlock code={`query: |
  find usage of any(
    "<div onClick={$HANDLER}>$...CHILDREN</div>",
    "<span onClick={$HANDLER}>$...CHILDREN</span>"
  )`} />

            <SubHeading>find pattern</SubHeading>
            <p className="vdr-body-text">Matches arbitrary code patterns with optional conditions:</p>
            <CodeBlock code={`query: |
  find pattern "$KEY = \\"$SECRET\\"" where $SECRET matches /^[A-Za-z0-9\\/+=]{16,}$/`} />

            <SubHeading>find annotation</SubHeading>
            <p className="vdr-body-text">Matches annotations and decorators:</p>
            <CodeBlock code={`query: |
  find annotation "Deprecated"`} />
          </>
        );

      /* ──────────────────────────────────────────────────────────────────── */
      case 'metavars':
        return (
          <>
            <p className="vdr-body-text">
              Metavariables are named placeholders in DSL patterns that match arbitrary
              sub-expressions in the target code. They are central to Vigil's pattern matching.
            </p>

            <SubHeading>Metavariable Syntax</SubHeading>
            <BulletList items={[
              <><code>$X</code>, <code>$VAR</code>, <code>$PATTERN</code> — match any single expression</>,
              <><code>$...ARGS</code> — match zero or more arguments (spread)</>,
              <><code>...</code> — ellipsis wildcard (match any arguments)</>,
              <><code>@concept</code> — concept reference, resolved per-language</>,
            ]} />

            <SubHeading>Expression AST</SubHeading>
            <p className="vdr-body-text">From <code>pkg/dsl/query.go</code>:</p>
            <CodeBlock code={`type Expr struct {
    Type     string  // "concept", "pattern", "any", "all", "ident"
    Value    string
    Children []*Expr
}`} />

            <SubHeading>Proven Patterns (from testing)</SubHeading>
            <Table
              headers={['', 'Pattern', 'Behavior']}
              rows={[
                [<span className="vdr-check">✅</span>, <code>{'find call to any("$X.method()")'}</code>, 'Method call with ANY receiver'],
                [<span className="vdr-check">✅</span>, <code>{'find call to any("new ClassName(...)")'}</code>, 'Constructor call'],
                [<span className="vdr-check">✅</span>, <code>{'find usage of any("text")'}</code>, 'AST identifier match'],
                [<span className="vdr-cross">❌</span>, <code>{'find call to any("method()")'}</code>, 'WITHOUT receiver does NOT match obj.method()'],
                [<span className="vdr-cross">❌</span>, <code>{'find usage of any("text()")'}</code>, 'Adding parens breaks the match'],
              ]}
            />
          </>
        );

      /* ──────────────────────────────────────────────────────────────────── */
      case 'ruleSpec':
        return (
          <>
            <p className="vdr-body-text">
              Each rule is defined in a YAML file with a well-defined structure.
              All fields are validated by the validation pipeline before compilation.
            </p>

            <CodeBlock code={`rule <rule-id>:
  version: "1.0.0"              # Semantic version (required)
  severity: high                # critical|high|medium|low|info (required)
  confidence: medium            # high|medium|low (required)
  languages: [go, java]         # Target languages (required, min 1)
  cwe: ["CWE-89"]              # CWE identifiers (optional)
  owasp: ["A03:2021"]          # OWASP tags (optional)

  query: |                      # DSL query (required)
    find call to any(...)

  exclude:
    paths: ["**/tests/**"]      # Path exclusions (optional)

  report:
    message: "..."              # Finding message (required)
    fix: "..."                  # Remediation guidance (optional)
    evidence: [data_flow]       # Evidence fields (optional)

  engineHints:                  # Engine-specific overrides (optional)
    opengrep:
      mode: "taint"
      options: "..."
    joern:
      query: "cpg.call(...).l"
    rascal:
      module: "..."
    treesitter:
      query: "(function_definition ...)"`} />

            <SubHeading>Field Reference</SubHeading>
            <Table
              headers={['Field', 'Required', 'Type', 'Description']}
              rows={[
                [<code>version</code>,    'Yes', 'string',   'Semantic version of the rule'],
                [<code>severity</code>,   'Yes', 'enum',     'critical | high | medium | low | info'],
                [<code>confidence</code>, 'Yes', 'enum',     'high | medium | low'],
                [<code>languages</code>,  'Yes', 'string[]', 'Target languages (min 1)'],
                [<code>cwe</code>,        'No',  'string[]', 'CWE identifiers (CWE-NNN)'],
                [<code>owasp</code>,      'No',  'string[]', 'OWASP Top 10 tags'],
                [<code>query</code>,      'Yes', 'string',   'DSL query expression'],
                [<code>exclude.paths</code>, 'No', 'string[]', 'Glob path exclusions'],
                [<code>report.message</code>, 'Yes', 'string', 'Finding message for output'],
                [<code>report.fix</code>,     'No',  'string', 'Remediation guidance'],
                [<code>engineHints</code>,    'No',  'object', 'Per-engine overrides'],
              ]}
            />
          </>
        );

      /* ──────────────────────────────────────────────────────────────────── */
      case 'validation':
        return (
          <>
            <p className="vdr-body-text">
              All rules pass through a strict validation pipeline before compilation.
              The validator is implemented in <code>pkg/rule/validator.go</code> with the key
              function <code>Validate(r *Rule) error</code>.
            </p>

            <SubHeading>Validation Checks</SubHeading>
            <Table
              headers={['Level', 'Check', 'Constraint']}
              rows={[
                ['Rule-level',       'id required',              'Non-empty string'],
                ['Rule-level',       'apiVersion',               'Must be "vigil/v1alpha1"'],
                ['Rule-level',       'kind',                     'Must be "Rule"'],
                ['Rule-level',       'metadata.name',            'Required, non-empty'],
                ['Spec-level',       'severity',                 'One of: critical, high, medium, low, info'],
                ['Spec-level',       'confidence',               'One of: high, medium, low'],
                ['Spec-level',       'languages',                'Min 1 language required'],
                ['Spec-level',       'message',                  'Required, non-empty'],
                ['Spec-level',       'expression',               'At least one field set'],
                ['CWE format',       'CWE identifiers',          '"CWE-NNN" pattern'],
                ['Expression',       'recursive validation',     'All expression nodes validated'],
              ]}
            />

            <SubHeading>Validation Results</SubHeading>
            <div className="vdr-stat-row">
              <div className="vdr-stat-card">
                <div className="vdr-stat-value" style={{ color: '#16a34a' }}>997</div>
                <div className="vdr-stat-label">Pass</div>
              </div>
              <div className="vdr-stat-card">
                <div className="vdr-stat-value" style={{ color: '#dc2626' }}>1</div>
                <div className="vdr-stat-label">Fail</div>
              </div>
              <div className="vdr-stat-card">
                <div className="vdr-stat-value">998</div>
                <div className="vdr-stat-label">Total</div>
              </div>
            </div>
            <p className="vdr-body-text" style={{ fontSize: '0.85rem', color: 'var(--bs-secondary-color)' }}>
              997/998 rules pass validation. The single failure is a pre-existing broken AI-generated rule.
            </p>
          </>
        );

      /* ──────────────────────────────────────────────────────────────────── */
      case 'compilation':
        return (
          <>
            <p className="vdr-body-text">
              The compilation pipeline translates the Vigil DSL into engine-specific formats.
              This is a two-step process: DSL → Internal Expression → Engine Format.
            </p>

            <SubHeading>Step 1: DSL → Internal Expression</SubHeading>
            <CodeBlock code={`DSL: find call to any("db.Query(...)", "db.Exec(...)")
  ↓ ParseQuery()
Query: FindType="call", Target=Expr{Type:"any", Children:[...]}
  ↓ queryToExpression()
Expression: Expression{Any: [
    Expression{Pattern: "db.Query(...)"},
    Expression{Pattern: "db.Exec(...)"}
]}`} />

            <SubHeading>Step 2: Expression → Engine Format</SubHeading>
            <p className="vdr-body-text">
              Opengrep is the primary compilation target (<code>pkg/compiler/opengrep.go</code>, 657 lines):
            </p>
            <Table
              headers={['DSL Construct', 'Opengrep Output']}
              rows={[
                [<code>{'find call to "func(...)"'}</code>,  <code>{'pattern: func(...)'}</code>],
                [<code>{'find call to any(...)'}</code>,      <code>{'pattern-either: [...]'}</code>],
                [<code>{'all(expr1, expr2)'}</code>,          <code>{'patterns: [...]'}</code>],
                [<code>{'not expr'}</code>,                   <code>{'pattern-not: ...'}</code>],
                [<code>{'flows-to relation'}</code>,          <code>{'pattern-sources/sinks/sanitizers'}</code>],
                [<code>{'exclude: paths'}</code>,             <code>{'path-exclude: [...]'}</code>],
              ]}
            />

            <SubHeading>Complete Example</SubHeading>
            <p className="vdr-body-text">DSL input:</p>
            <CodeBlock code={`query: |
  find call to any("db.Query(...)", "db.Exec(...)")`} />
            <p className="vdr-body-text">Compiles to Opengrep:</p>
            <CodeBlock code={`rules:
  - id: sql-injection
    message: "SQL injection detected"
    severity: WARNING
    languages: [python]
    pattern-either:
      - pattern: db.Query(...)
      - pattern: db.Exec(...)`} />
          </>
        );

      /* ──────────────────────────────────────────────────────────────────── */
      case 'engines':
        return (
          <>
            <p className="vdr-body-text">
              Vigil supports 4 analysis engines, each with different strengths. The engine
              selector automatically chooses the best engine based on query type capabilities.
            </p>

            <SubHeading>Engine Overview</SubHeading>
            <Table
              headers={['Engine', 'Type', 'Status', 'Primary Strength']}
              rows={[
                [<span className="vdr-engine-badge">Opengrep</span>,    'Regex/pattern-based', '✅ Primary',   'Pattern matching, syntax'],
                [<span className="vdr-engine-badge">Joern</span>,       'CPG/graph-based',     '✅ Supported', 'Data-flow analysis'],
                [<span className="vdr-engine-badge">TreeSitter</span>,  'AST/parse-tree',      '✅ Supported', 'AST matching'],
                [<span className="vdr-engine-badge">Rascal</span>,      'Metaprogramming',     '✅ Supported', 'Grammar transforms'],
              ]}
            />

            <SubHeading>Capability Scores (from pkg/engine/selector.go)</SubHeading>
            <p className="vdr-body-text" style={{ fontSize: '0.85rem' }}>
              <span className="vdr-cap-optimal">🟣 Optimal (3)</span>{' · '}
              <span className="vdr-cap-good">🟢 Good (2)</span>{' · '}
              <span className="vdr-cap-fallback">🟡 Fallback (1)</span>{' · '}
              <span className="vdr-cap-na">🔴 N/A (0)</span>
            </p>
            <Table
              headers={['Find Type', 'Opengrep', 'Joern', 'TreeSitter', 'Rascal']}
              rows={[
                ['pattern',    '🟣', '🟡', '🟡', '🟢'],
                ['call',       '🟣', '🟢', '🟢', '🟢'],
                ['usage',      '🟣', '🟢', '🟡', '🟢'],
                ['flow',       '🟢', '🟣', '🔴', '🟢'],
                ['literal',    '🟣', '🟡', '🟢', '🟢'],
                ['function',   '🟢', '🟢', '🟣', '🟣'],
                ['import',     '🟣', '🟢', '🟣', '🟢'],
                ['class',      '🟢', '🟣', '🟣', '🟣'],
                ['annotation', '🟣', '🟢', '🟣', '🟢'],
                ['variable',   '🟢', '🟢', '🟣', '🟢'],
                ['assignment', '🟢', '🟣', '🟣', '🟢'],
              ]}
              mono={[0]}
            />

            <SubHeading>Auto-Selection Logic</SubHeading>
            <p className="vdr-body-text">
              Engine selection priority: <strong>EngineHints override</strong> → <strong>Best capability match</strong> → <strong>Fallback to Opengrep</strong>.
            </p>
          </>
        );

      /* ──────────────────────────────────────────────────────────────────── */
      case 'scanning':
        return (
          <>
            <p className="vdr-body-text">
              The end-to-end scanning pipeline orchestrates rule loading, engine selection,
              compilation, execution, and result normalization.
            </p>

            <SubHeading>Pipeline Steps</SubHeading>
            <BulletList items={[
              <><strong>1. CLI Input</strong> — target directory, rules directory, engine selection</>,
              <><strong>2. LoadRulesFromDir()</strong> — recursively scans for .rule and .json files</>,
              <><strong>3. Engine Selection</strong> — auto or explicit, via selector.go</>,
              <><strong>4. Rule Compilation</strong> — Compiler.Compile(rule) → engine-specific format</>,
              <><strong>5. Engine Execution</strong> — write compiled rules to temp dir, invoke engine binary</>,
              <><strong>6. Result Parsing</strong> — parse SARIF output, normalize findings</>,
              <><strong>7. Result Enrichment</strong> — merge duplicates, add context, filter by severity</>,
            ]} />

            <SubHeading>Opengrep Invocation</SubHeading>
            <CodeBlock code={`args := []string{"scan", "--config", rulesDir, "--sarif", 
                  "--output", outputPath, "--no-git-ignore"}`} />

            <SubHeading>Engine Configuration</SubHeading>
            <Table
              headers={['Setting', 'Value']}
              rows={[
                ['Binary search order', <code>opengrep → osemgrep → semgrep</code>],
                ['Default timeout',     '5 minutes (configurable)'],
                ['Output format',       'SARIF'],
                ['Git ignore',          'Disabled (--no-git-ignore)'],
              ]}
            />
          </>
        );

      /* ──────────────────────────────────────────────────────────────────── */
      case 'inventory':
        return (
          <>
            <p className="vdr-body-text">
              Comprehensive analysis of the 998-rule corpus across 99 rule packs.
            </p>

            <SubHeading>Top 20 Rule Packs</SubHeading>
            <Table
              headers={['Pack', 'Rules', 'Languages', 'Focus']}
              rows={[
                [<code>js-extended</code>,           '28', 'JS/TS',  'Extended JavaScript security'],
                [<code>rails-extended</code>,        '25', 'Ruby',   'Rails framework'],
                [<code>php-extended</code>,           '25', 'PHP',    'PHP security'],
                [<code>python-extended</code>,        '22', 'Python', 'Extended Python security'],
                [<code>framework-security</code>,     '21', 'Multi',  'Cross-framework'],
                [<code>wordpress-security</code>,     '20', 'PHP',    'WordPress'],
                [<code>symfony-security</code>,       '20', 'PHP',    'Symfony'],
                [<code>ruby-security</code>,          '20', 'Ruby',   'Generic Ruby'],
                [<code>csharp-security</code>,        '20', 'C#',     '.NET security'],
                [<code>c-security-extended</code>,    '20', 'C',      'Extended C security'],
                [<code>aspnet-extended</code>,        '20', 'C#',     'ASP.NET'],
                [<code>owasp-top-10</code>,           '19', 'Multi',  'OWASP Top 10'],
                [<code>nestjs-extended</code>,        '15', 'TS',     'NestJS framework'],
                [<code>js-dom-security</code>,        '15', 'JS/TS',  'DOM security'],
                [<code>ef-core-security</code>,       '15', 'C#',     'Entity Framework'],
                [<code>cpp-security-extended</code>,  '15', 'C++',    'Extended C++'],
                [<code>js-injection-security</code>,  '14', 'JS/TS',  'Injection attacks'],
                [<code>lang-go</code>,                '13', 'Go',     'Go security'],
                [<code>jakarta-ee</code>,             '13', 'Java',   'Jakarta EE'],
                [<code>ts-type-safety</code>,         '12', 'TS',     'Type safety'],
              ]}
              mono={[1]}
            />

            <SubHeading>Severity Distribution</SubHeading>
            <Table
              headers={['Severity', 'Count', '%']}
              rows={[
                [<span className="vdr-badge vdr-badge-critical">Critical</span>, '188', '18.8%'],
                [<span className="vdr-badge vdr-badge-high">High</span>,         '456', '45.7%'],
                [<span className="vdr-badge vdr-badge-medium">Medium</span>,     '299', '30.0%'],
                [<span className="vdr-badge vdr-badge-low">Low</span>,            '31',  '3.1%'],
                [<span className="vdr-badge vdr-badge-info">Info</span>,           '24',  '2.4%'],
              ]}
              mono={[1]}
            />

            <SubHeading>Top 15 CWE Mappings</SubHeading>
            <Table
              headers={['CWE', 'Count', 'Description']}
              rows={[
                [<span className="vdr-cwe-badge">CWE-89</span>,  '96', 'SQL Injection'],
                [<span className="vdr-cwe-badge">CWE-79</span>,  '86', 'Cross-site Scripting'],
                [<span className="vdr-cwe-badge">CWE-798</span>, '71', 'Hard-coded Credentials'],
                [<span className="vdr-cwe-badge">CWE-502</span>, '66', 'Unsafe Deserialization'],
                [<span className="vdr-cwe-badge">CWE-400</span>, '46', 'Resource Consumption'],
                [<span className="vdr-cwe-badge">CWE-601</span>, '45', 'URL Redirection'],
                [<span className="vdr-cwe-badge">CWE-22</span>,  '43', 'Path Traversal'],
                [<span className="vdr-cwe-badge">CWE-20</span>,  '43', 'Input Validation'],
                [<span className="vdr-cwe-badge">CWE-94</span>,  '40', 'Code Injection'],
                [<span className="vdr-cwe-badge">CWE-78</span>,  '39', 'OS Command Injection'],
                [<span className="vdr-cwe-badge">CWE-942</span>, '36', 'CORS Policy'],
                [<span className="vdr-cwe-badge">CWE-918</span>, '34', 'SSRF'],
                [<span className="vdr-cwe-badge">CWE-352</span>, '33', 'CSRF'],
                [<span className="vdr-cwe-badge">CWE-489</span>, '29', 'Debug Code'],
                [<span className="vdr-cwe-badge">CWE-327</span>, '28', 'Weak Crypto'],
              ]}
              mono={[1]}
            />

            <SubHeading>OWASP Top 10 Coverage</SubHeading>
            <Table
              headers={['Category', 'Count']}
              rows={[
                ['A03 Injection',            '228'],
                ['A05 Access Control',       '130'],
                ['A01 Authentication',       '124'],
                ['A02 Crypto Failures',      '107'],
                ['A07 XSS',                   '89'],
                ['A04 Insecure Design',       '73'],
                ['A08 Data Integrity',        '44'],
                ['A10 SSRF',                  '23'],
                ['A09 Known Vulns',           '23'],
                ['A06 Outdated Components',   '10'],
              ]}
              mono={[1]}
            />
          </>
        );

      /* ──────────────────────────────────────────────────────────────────── */
      case 'dataStructures':
        return (
          <>
            <p className="vdr-body-text">
              The core Go types that drive the rule engine. These structures are the internal
              representation used throughout parsing, validation, compilation, and execution.
            </p>

            <SubHeading>Rule Model (pkg/rule/types.go)</SubHeading>
            <CodeBlock code={`type Rule struct {
    APIVersion string
    Kind       string
    ID         string
    Version    string
    Metadata   Metadata
    Spec       RuleSpec
}

type RuleSpec struct {
    Severity      string
    Confidence    string
    Languages     []string
    CWE           []string
    OWASP         []string
    Expression    Expression
    Within        *Expression
    Paths         []string
    ExcludePaths  []string
    Message       string
    Remediation   string
    Evidence      []Evidence
    EngineHints   *EngineHints
    FindType      string
}

type Expression struct {
    Concept   string
    Ref       string
    Pattern   string
    Relation  *Relation
    All       []Expression
    Any       []Expression
    Not       *Expression
}

type Relation struct {
    Type       string  // "flows-to", "follows", "contains"
    Source     Expression
    Sink       Expression
    Sanitizer  Expression
}`} />

            <SubHeading>Query AST (pkg/dsl/query.go)</SubHeading>
            <CodeBlock code={`type Query struct {
    FindType  string
    Target    *Expr
    From      *Expr
    To        *Expr
    Where     *Condition
    Within    *Expr
    Languages []string
    FilePath  string
}

type Condition struct {
    Type    string  // "and","or","not","uses","passes_through","calls","matches","extends","implements"
    Left    *Condition
    Right   *Condition
    Operand *Condition
    Expr    *Expr
    Pattern string
}`} />
          </>
        );

      /* ──────────────────────────────────────────────────────────────────── */
      case 'taint':
        return (
          <>
            <p className="vdr-body-text">
              Taint analysis tracks data flow from sources (user input) through the program
              to sinks (dangerous operations), identifying paths that bypass sanitizers.
            </p>

            <SubHeading>DSL Syntax</SubHeading>
            <CodeBlock code={`find flow from @source to @sink where not passes_through @sanitizer`} />

            <SubHeading>Compilation Output</SubHeading>
            <p className="vdr-body-text">
              The flow query compiles to three pattern groups:
            </p>
            <Table
              headers={['Pattern Group', 'Purpose', 'Example']}
              rows={[
                [<code>pattern-sources</code>,    'Entry points for tainted data',   'request.body, request.params'],
                [<code>pattern-sinks</code>,      'Dangerous destinations',          'db.query(...), exec(...)'],
                [<code>pattern-sanitizers</code>, 'Functions that clean data',       'escape(...), sanitize(...)'],
              ]}
            />

            <SubHeading>Engine Capability for Taint</SubHeading>
            <Table
              headers={['Engine', 'Taint Support', 'Notes']}
              rows={[
                [<span className="vdr-engine-badge">Joern</span>,      <span className="vdr-cap-optimal">🟣 Optimal</span>, 'Uses CPG data-flow graph; full inter-procedural tracking'],
                [<span className="vdr-engine-badge">Opengrep</span>,   <span className="vdr-cap-good">🟢 Good</span>,       'Supports taint mode; intra-file analysis'],
                [<span className="vdr-engine-badge">Rascal</span>,     <span className="vdr-cap-good">🟢 Good</span>,       'Grammar-based flow tracking'],
                [<span className="vdr-engine-badge">TreeSitter</span>, <span className="vdr-cap-na">🔴 N/A</span>,          'CANNOT do flow analysis (score: 0)'],
              ]}
            />
          </>
        );

      /* ──────────────────────────────────────────────────────────────────── */
      case 'concepts':
        return (
          <>
            <p className="vdr-body-text">
              The concept system provides an abstraction layer that decouples security rules
              from language-specific APIs. A single <code>@concept_name</code> reference resolves
              to concrete patterns per target language.
            </p>

            <SubHeading>Resolution Mechanism</SubHeading>
            <BulletList items={[
              <><code>@concept_name</code> references abstract security patterns</>,
              'Resolved per-language to concrete patterns at compilation time',
              <>Stored in <code>pkg/concept/{'{'+'mapping,catalog'+'}'}.go</code></>,
              'Enables single rule to work across multiple languages',
            ]} />

            <SubHeading>Example: @sql_query</SubHeading>
            <CodeBlock code={`@sql_query resolves to:
  python:     ["db.execute(...)", "db.session.execute(...)"]
  javascript: ["client.query(...)", "db.run(...)"]
  java:       ["Statement.execute(...)", "PreparedStatement.executeQuery(...)"]`} />

            <SubHeading>Usage in Rules</SubHeading>
            <CodeBlock code={`query: |
  find call to @sql_query
  where uses($USER_INPUT)

# Equivalent to writing separate rules for each language,
# but with a single, maintainable definition.`} />
          </>
        );

      /* ──────────────────────────────────────────────────────────────────── */
      case 'decisions':
        return (
          <>
            <p className="vdr-body-text">
              Key architectural decisions and the rationale behind them:
            </p>

            <Table
              headers={['#', 'Decision', 'Rationale']}
              rows={[
                ['1', <strong>Single DSL, multiple engines</strong>,
                  'Write once, run on any engine. Engine selection is automatic based on capabilities. Avoids vendor lock-in.'],
                ['2', <strong>Opengrep as primary engine</strong>,
                  'Pattern matching covers 95%+ of rules. Optimal for call/usage/pattern queries. Battle-tested at scale.'],
                ['3', <strong>Joern for data-flow</strong>,
                  'CPG-based analysis excels at taint tracking. Used when flows-to relations are present. Complementary to Opengrep.'],
                ['4', <strong>YAML rule format</strong>,
                  'Human-readable, version-controllable, easy to review in pull requests. Low barrier to rule authoring.'],
                ['5', <strong>Metavariable preservation</strong>,
                  '$X patterns pass through to engines unchanged, leveraging native metavar support. Zero translation overhead.'],
                ['6', <strong>Concept abstraction</strong>,
                  '@concepts decouple rules from language specifics, enabling multi-language rules from a single definition.'],
                ['7', <strong>Validation-first pipeline</strong>,
                  'All rules validated before compilation to catch errors early. Fast feedback loop for rule authors.'],
              ]}
            />

            <SubHeading>Trade-offs Accepted</SubHeading>
            <BulletList items={[
              'Opengrep fallback means some data-flow rules have lower precision than Joern — acceptable for coverage.',
              'Hand-written parser over parser-generator: more code, but full control over error messages and recovery.',
              'YAML over a custom format: slightly more verbose, but universal tooling support (editors, linters, CI).',
              'Per-engine compilers increase maintenance surface — mitigated by the shared Expression IR.',
            ]} />
          </>
        );

      /* ──────────────────────────────────────────────────────────────────── */
      default:
        return <p className="vdr-body-text">Section content not available.</p>;
    }
  };

  const now = new Date();
  const timestamp = now.toISOString().replace('T', ' ').slice(0, 16) + ' UTC';

  return (
    <div className="vdr-root">
      {/* Header */}
      <div className="vdr-header">
        <div className="vdr-header-row">
          <div>
            <h1 className="vdr-title">🛡️ Vigil DSL Design Report</h1>
            <p className="vdr-subtitle">
              Architecture & specification document for expert technical review
            </p>
            <div className="vdr-stamp">
              Vigil DSL Design Report v1.0 — Generated for Expert Review
            </div>
            <div className="vdr-stamp" style={{ marginLeft: '0.5rem' }}>
              Last updated: {timestamp}
            </div>
          </div>
          <button className="vdr-print-btn" onClick={() => window.print()}>
            🖨️ Export PDF
          </button>
        </div>
      </div>

      {/* 2-column layout */}
      <div className="vdr-layout">
        {/* TOC sidebar */}
        <div className="vdr-toc">
          <div className="vdr-toc-title">Contents</div>
          {SECTIONS.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              className={activeId === section.id ? 'active' : ''}
            >
              {section.icon} {section.title}
            </a>
          ))}
        </div>

        {/* Content */}
        <div className="vdr-content">
          {SECTIONS.map((section) => (
            <section key={section.id} id={section.id}>
              <SectionHeading icon={section.icon} title={section.title} />
              {renderSection(section)}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
